import { Card, Select, TableV2, TextInput } from '@medeloop/medeloop-components-library';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useGetDataSourceById } from 'src/api/data-sources-queries';
import { capitalizeFirstLetter, formatDateRange } from 'src/utils/datasets';
import CompletenessBar from '../charts/completeness-bar';
import { SkeletonLine } from '../skeleton-line/skeleton-line';
import { DataSourceWiki } from 'src/api/data-sources';

const formatLabel = (label: string) => {
  const split = label.split('_').join(' ');
  // Capitalize all words
  return split.replace(/\b\w/g, (char) => char.toUpperCase());
};
export const DatasetOverview = () => {
  const { t } = useTranslation();
  const { datasetId } = useParams();
  const [tableSearchInput, setTableSearchInput] = useState<string>('');
  const [currentTable, setCurrentTable] = useState<string>('');
  const { data: dataSource, isLoading } = useGetDataSourceById(datasetId ?? '');

  const localDataSourceWiki = useMemo(
    () => (dataSource?.dataWiki || {}) as DataSourceWiki,
    [dataSource],
  );
  const { metadata } = localDataSourceWiki || {};
  const { date_range, record_count, provider_name, link, tables = [] } = metadata || {};
  const transFormedTableData = useMemo(
    () =>
      (tables.find((table) => table.name === currentTable)?.columns || [])
        .filter(
          (column) =>
            column.name.toLowerCase().includes(tableSearchInput.toLowerCase()) ||
            column.description.toLowerCase().includes(tableSearchInput.toLowerCase()) ||
            column.type.toLowerCase().includes(tableSearchInput.toLowerCase()),
        )
        .map((column, index) => ({
          ...column,
          description: (
            <p key={`${column}-${index}`} className="max-w-[400px]">
              {capitalizeFirstLetter(column.description)}
            </p>
          ),
          completeness: <CompletenessBar percentage={column.completeness_rate || 100} />,
        })),
    [tables, currentTable, tableSearchInput],
  );

  useEffect(() => {
    if (localDataSourceWiki?.metadata?.tables) {
      setCurrentTable(localDataSourceWiki?.metadata?.tables[0].name);
    }
  }, [localDataSourceWiki, isLoading]);

  return (
    <section className="flex flex-col w-full">
      <div className="flex gap-4 ">
        <Card variant="Outlined" size="md" className="h-auto">
          <span className="text-sm">{t('time_span')}</span>
          <p className="text-lg font-medium text-content-on-dark-default mt-2">
            {formatDateRange(date_range)}
          </p>
        </Card>
        <Card variant="Outlined" size="md" className="h-auto">
          <span className="text-sm">{t('record_count')}</span>
          <p className="text-lg font-medium text-content-on-dark-default mt-2">{record_count}</p>
        </Card>
        <Card variant="Outlined" size="md" className="h-auto">
          <span className="text-sm">{t('data_provider')}</span>
          <p className="text-lg font-medium text-content-on-dark-default mt-2">
            <span>{provider_name}</span>
            {link && (
              <Link to={link} className="text-cyan-300 text-sm ml-4" target="_blank">
                {t('learn_more')}
              </Link>
            )}
          </p>
        </Card>
      </div>
      <div className="flex flex-col w-full mt-4">
        <p className="text-lg text-content-on-dark-default mb-4 font-[600]">
          {t('data_dictionary')}
        </p>
        <Card variant="Outlined" size="lg" className="mx-0 w-full">
          <div className="flex flex-col w-full">
            <div className="flex flex-row gap-2 justify-between">
              {isLoading ? (
                <div className="w-32">
                  <SkeletonLine />
                </div>
              ) : (
                <Select
                  options={(tables || []).map((table) => ({
                    label: formatLabel(table.name),
                    value: table.name,
                  }))}
                  placeholder={t('select_table')}
                  value={currentTable}
                  onValueChange={(value) => setCurrentTable(value)}
                  className="w-1/3"
                />
              )}
              <div className="w-1/3">
                <TextInput
                  placeholder={t('search_column_name_type_description')}
                  size="sm"
                  type="text"
                  value={tableSearchInput}
                  onChange={(e) => setTableSearchInput(e.target.value)}
                />
              </div>
            </div>
            <p className="text-sm text-content-on-dark-subdued">
              {tables.find((table) => table.name === currentTable)?.description}
            </p>
            <div className="mt-4 max-h-96 overflow-y-auto text-sm">
              <TableV2
                headers={[
                  { label: t('column_name').toUpperCase(), key: 'name' },
                  { label: t('column_type').toUpperCase(), key: 'type' },
                  { label: t('column_description').toUpperCase(), key: 'description' },
                  { label: t('column_completeness_score').toUpperCase(), key: 'completeness' },
                ]}
                isLoading={isLoading}
                data={transFormedTableData}
              />
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
};
